<template>
  <PropertyOnboardingCard :class="{ loading: isLoading }">
    <template #header>
      <View justify="end">
        <Button variant="ghost" @click="exit">
          {{ property ? 'Quit & save' : 'Exit' }}
        </Button>
      </View>
    </template>

    <template #body>
      <Text variant="h1" class="title">{{ title }}</Text>

      <View direction="column" align="center" gap="md" :grow="1">
        <Card
          v-for="stage in stages"
          :key="stage.title"
          :class="{ available: canNavigateToStage(stage.stageName) }"
          @click="openStage(stage.stageName)"
          variant="outline"
          size="medium"
          class="stage"
        >
          <View gap="md" :grow="1">
            <IconTile :icon="stage.icon" :color="stage.iconColor" />

            <div class="stage-title">
              <Text variant="h4">{{ stage.title }}</Text>
              <Text variant="body-large" color="tertiary">{{ getStageSteps(stage.stageName).length }} steps</Text>
            </div>

            <Icon name="check-circle" v-if="isStageCompteled(stage.stageName)" class="check-icon-container" />
          </View>
        </Card>
      </View>
    </template>

    <template #footer>
      <View justify="end" :grow="1">
        <Button variant="primary" @click="next">{{ property ? 'Continue' : 'Get started' }}</Button>
      </View>
    </template>
  </PropertyOnboardingCard>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { Icon, Button, Text, View, IconTile, Card } from '@tallo/design-system';
import { AppRouteNames } from '@tallo/investor/navigation';
import { usePropertyStore } from '@tallo/investor/property';

import { PropertyOnboardingStage } from '../configs/stages.enum';
import { usePropertyOnboardingStore } from '../store/property-onboarding.store';
import PropertyOnboardingCard from './property-onboarding-card.vue';

const router = useRouter();
const propertyStore = usePropertyStore();
const propertyOnboardingStore = usePropertyOnboardingStore();
const { getStages, getStageSteps, isStepCompleted, isStageCompteled, getStagesWithMetaData } =
  storeToRefs(propertyOnboardingStore);
const property = computed(() => propertyStore.property);
const isLoading = computed(() => propertyStore.isLoading);
const stages = computed(() => getStagesWithMetaData.value);

const title = computed(() => {
  const completeStages = stages.value.filter((stage) => isStageCompteled.value(stage.stageName));

  switch (completeStages.length) {
    case 0:
      return 'List your property with Tallo';

    case stages.value.length - 1:
      return 'One more step to finish your listing';

    default:
      return `Complete ${stages.value.length - completeStages.length} more steps`;
  }
});

function next() {
  const propertyId = propertyStore?.property?.id;

  if (!propertyId) {
    const firstStageName = stages.value[0].stageName;
    const firstStepName = getStageSteps.value(firstStageName)[0];

    propertyOnboardingStore.navigateToStep(firstStepName);
  }

  const firstIncompleteStage = getStages.value.find((stage) => !isStageCompteled.value(stage));

  if (!firstIncompleteStage) {
    return router.push({
      name: AppRouteNames.INVESTOR_PROPERTY_DETAILS,
      params: { propertyId },
    });
  }

  const firstIncompleteStageSteps = getStageSteps.value(firstIncompleteStage);
  const firstIncompleteStepIndex = firstIncompleteStageSteps.findIndex((step) => !isStepCompleted.value(step));
  const stepIndex = firstIncompleteStepIndex !== -1 ? firstIncompleteStepIndex : 0;
  const step = firstIncompleteStageSteps[stepIndex];

  propertyOnboardingStore.navigateToStep(step);
}

function exit() {
  router.push('/');
}

function openStage(stageName: PropertyOnboardingStage) {
  const stage = stages.value.find((stage) => stage.stageName === stageName);

  // if stage is completed, or previous stage is completed, or it is the first stage then navigate to the stage
  if (stage && canNavigateToStage(stageName)) {
    const steps = getStageSteps.value(stageName);

    propertyOnboardingStore.navigateToStep(steps[0]);
  }
}

function canNavigateToStage(stageName: PropertyOnboardingStage) {
  const isFirstStage = propertyOnboardingStore.isFirstStage(stageName);
  const isStageCompleted = isStageCompteled.value(stageName);
  const prevStage = stages.value[stages.value.findIndex((stage) => stage.stageName === stageName) - 1];
  const isPrevCompleted = isStageCompteled.value(prevStage?.stageName);

  return isFirstStage || isStageCompleted || isPrevCompleted;
}
</script>

<style scoped lang="scss">
.loading {
  display: none;
}

.title {
  text-align: center;
  margin-top: 1rem;
  margin-bottom: 2rem;
}

.stage {
  width: 100%;
  max-width: 32rem;

  &.available {
    cursor: pointer;
  }

  .stage-title {
    flex-grow: 1;
  }

  .check-icon-container {
    flex-shrink: 0;
    align-self: center;
    color: var(--t-color-green-500);
  }
}
</style>
